import {useEffect, useMemo, useState} from 'react'
import {Button, Card, Space, Typography} from 'antd'
import {BugOutlined} from '@ant-design/icons'
import {useTranslation} from 'react-i18next'
import {collectRuntimeDiagnostics} from './appRuntime.js'
import {getRuntimeDebugState, subscribeRuntimeDebugState} from './debugState.js'

const {Text} = Typography

function isDebugModeEnabled() {
  if (typeof window === 'undefined') {
    return false
  }

  const params = new URLSearchParams(window.location.search)
  const queryEnabled = params.get('debug_runtime') === '1'

  if (queryEnabled) {
    try {
      window.localStorage.setItem('__app_debug_runtime', '1')
    } catch {
      // ignore storage failures
    }
  }

  if (import.meta.env.DEV) {
    return true
  }

  try {
    return window.localStorage.getItem('__app_debug_runtime') === '1'
  } catch {
    return queryEnabled
  }
}

function DebugLine({label, value}) {
  return (
    <div style={{display: 'flex', justifyContent: 'space-between', gap: 12}}>
      <Text type="secondary">{label}</Text>
      <Text code style={{whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 180}}>
        {value || 'n/a'}
      </Text>
    </div>
  )
}

export default function RuntimeDebugPanel() {
  const {t} = useTranslation()
  const [enabled] = useState(() => isDebugModeEnabled())
  const [open, setOpen] = useState(() => isDebugModeEnabled())
  const [debugState, setDebugState] = useState(() => getRuntimeDebugState())

  useEffect(() => {
    if (!enabled) {
      return undefined
    }

    return subscribeRuntimeDebugState((nextState) => {
      setDebugState(nextState)
    })
  }, [enabled])

  const diagnostics = useMemo(() => collectRuntimeDiagnostics(), [debugState])

  if (!enabled) {
    return null
  }

  return (
    <div
      style={{
        position: 'fixed',
        right: 12,
        bottom: 12,
        zIndex: 1600,
        maxWidth: 360,
        width: 'calc(100vw - 24px)',
      }}
    >
      {!open && (
        <Button
          type="primary"
          size="small"
          icon={<BugOutlined />}
          onClick={() => setOpen(true)}
        >
          {t('appRuntime.debug.toggle')}
        </Button>
      )}

      {open && (
        <Card
          size="small"
          title={t('appRuntime.debug.title')}
          extra={<Button type="text" size="small" onClick={() => setOpen(false)}>{t('appRuntime.debug.hide')}</Button>}
          styles={{body: {padding: 12}}}
        >
          <Space direction="vertical" size="small" style={{width: '100%'}}>
            <DebugLine label={t('appRuntime.debug.buildVersion')} value={diagnostics.buildVersion} />
            <DebugLine label={t('appRuntime.debug.isStandalone')} value={String(diagnostics.isStandalone)} />
            <DebugLine label={t('appRuntime.debug.online')} value={String(diagnostics.online)} />
            <DebugLine label={t('appRuntime.debug.swState')} value={diagnostics.serviceWorker?.state} />
            <DebugLine label={t('appRuntime.debug.authHydrated')} value={String(debugState.authHydrated === true)} />
            <DebugLine
              label={t('appRuntime.debug.authState')}
              value={diagnostics.auth?.isAuthenticated ? t('appRuntime.debug.authenticated') : t('appRuntime.debug.guest')}
            />
            <DebugLine label={t('appRuntime.debug.bootAt')} value={debugState.appBootTimestamp} />
            <DebugLine label={t('appRuntime.debug.lastEvent')} value={debugState.lastEvent?.type} />
            <DebugLine label={t('appRuntime.debug.lastError')} value={debugState.lastRequestError?.message} />
          </Space>
        </Card>
      )}
    </div>
  )
}
